// UK English localisation utilities

export const ukSpelling = {
  // Common American to British spelling conversions
  'optimize': 'optimise',
  'optimization': 'optimisation',
  'analyze': 'analyse',
  'analysis': 'analysis', // Same in both
  'color': 'colour',
  'flavor': 'flavour',
  'honor': 'honour',
  'center': 'centre',
  'theater': 'theatre',
  'organize': 'organise',
  'organization': 'organisation',
  'realize': 'realise',
  'customize': 'customise',
  'customization': 'customisation',
  'license': 'licence', // noun
  'practice': 'practise', // verb
  'defense': 'defence',
  'offense': 'offence'
};

export const ukCurrency = {
  symbol: '£',
  code: 'GBP',
  name: 'British Pound Sterling'
};

export const ukBusinessTerms = {
  'company': 'company',
  'corporation': 'limited company',
  'LLC': 'Ltd',
  'Inc': 'Ltd',
  'sales tax': 'VAT',
  'zip code': 'postcode',
  'state': 'county',
  'cell phone': 'mobile phone',
  'elevator': 'lift',
  'apartment': 'flat'
};

export const ukPhoneFormat = {
  // Format: +44 20 1234 5678 or 020 1234 5678
  pattern: /^(\+44\s?|0)([1-9]\d{8,9})$/,
  format: (number: string) => {
    const cleaned = number.replace(/\s/g, '');
    if (cleaned.startsWith('+44')) {
      return cleaned.replace(/(\+44)(\d{2})(\d{4})(\d{4})/, '$1 $2 $3 $4');
    } else if (cleaned.startsWith('0')) {
      return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1 $2 $3');
    }
    return number;
  }
};

export const ukDateFormat = {
  short: 'DD/MM/YYYY',
  long: 'DD Month YYYY',
  format: (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }
};

export const ukCompliance = {
  gdpr: {
    required: true,
    cookieConsent: 'This website uses cookies to ensure you get the best experience on our website.',
    privacyPolicy: 'View our Privacy Policy',
    dataProcessing: 'We process your data in accordance with UK GDPR regulations.'
  },
  vat: {
    rate: 0.20, // 20% standard rate
    display: 'All prices shown are exclusive of VAT',
    calculation: (amount: number) => amount * 1.20
  },
  companyRegistration: {
    format: 'Company Registration No: 12345678',
    required: true
  }
};

export const ukContentTone = {
  formal: {
    greeting: 'Good day',
    closing: 'Kind regards',
    politeness: 'please',
    gratitude: 'thank you very much'
  },
  professional: {
    greeting: 'Hello',
    closing: 'Best regards',
    politeness: 'please',
    gratitude: 'thank you'
  },
  friendly: {
    greeting: 'Hi there',
    closing: 'Cheers',
    politeness: 'please',
    gratitude: 'thanks'
  }
};

// Function to convert text to UK English
export function convertToUKEnglish(text: string): string {
  let ukText = text;
  
  Object.entries(ukSpelling).forEach(([us, uk]) => {
    const regex = new RegExp(`\\b${us}\\b`, 'gi');
    ukText = ukText.replace(regex, uk);
  });
  
  Object.entries(ukBusinessTerms).forEach(([us, uk]) => {
    const regex = new RegExp(`\\b${us}\\b`, 'gi');
    ukText = ukText.replace(regex, uk);
  });
  
  // Replace $ with £
  ukText = ukText.replace(/\$(\d+)/g, '£$1');
  
  return ukText;
}

// Format price in UK format
export function formatUKPrice(amount: number, includeVAT: boolean = false): string {
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
  
  if (includeVAT) {
    return `${formatted} (inc. VAT)`;
  }
  
  return `${formatted} (ex. VAT)`;
}

// UK postcode validation
export function validateUKPostcode(postcode: string): boolean {
  const ukPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
  return ukPostcodeRegex.test(postcode.replace(/\s/g, ''));
}

export default {
  ukSpelling,
  ukCurrency,
  ukBusinessTerms,
  ukPhoneFormat,
  ukDateFormat,
  ukCompliance,
  ukContentTone,
  convertToUKEnglish,
  formatUKPrice,
  validateUKPostcode
};